import { clientStorage } from "@/lib/clientStorage";
import { isLacwLocalTestMode, lacwLocalTestStore } from "@/lib/lacwLocalTestStore";

const STORAGE_KEY = "cls_ephemeral_matter_workspaces_v1";

function id(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function readAll() {
  try {
    const parsed = JSON.parse(clientStorage.getItem(STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(value) {
  clientStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

function readBrand(brand) {
  if (brand === "lacw" && isLacwLocalTestMode()) {
    return { all: null, workspace: lacwLocalTestStore.readWorkspace() };
  }
  const all = readAll();
  return {
    all,
    workspace: all[brand] || { matters: [], tasks: [], documents: [], timeline: [] },
  };
}

function saveBrand(brand, all, workspace) {
  if (brand === "lacw" && isLacwLocalTestMode()) {
    lacwLocalTestStore.writeWorkspace(workspace);
    return;
  }
  all[brand] = workspace;
  writeAll(all);
}

function record(workspace, matterId, action) {
  workspace.timeline.unshift({
    id: id("activity"),
    matter_id: matterId,
    timestamp: new Date().toISOString(),
    action,
    details: action,
  });
}

function linkedDrafts(brand, matterId) {
  const results = [];
  const seen = new Set();
  for (const [, raw] of clientStorage.entries()) {
    try {
      const value = JSON.parse(raw || "null");
      const candidates = Array.isArray(value) ? value : [value];
      for (const draft of candidates) {
        if (!draft || typeof draft !== "object" || !draft.id || seen.has(draft.id)) continue;
        const draftMatter = draft.matter_id
          || draft.form_data?.matter_id
          || draft.form_data?.matter_number
          || draft.form_data?.matterNumber
          || draft.form_data?.file_ref
          || draft.form_data?.file_no;
        if (String(draftMatter || "") !== String(matterId)) continue;
        if (draft.brand && draft.brand !== brand) continue;
        seen.add(draft.id);
        results.push(draft);
      }
    } catch {
      // Ignore non-JSON session settings.
    }
  }
  return results;
}

export const ephemeralMatterWorkspace = {
  list(brand) {
    return [...readBrand(brand).workspace.matters]
      .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
  },

  get(brand, matterId) {
    const { workspace } = readBrand(brand);
    const matter = workspace.matters.find((item) => item.id === matterId);
    if (!matter) throw new Error("Matter not found in this browser session");
    return {
      matter,
      audits: [],
      documents: workspace.documents.filter((item) => item.matter_id === matterId),
      drafts: linkedDrafts(brand, matterId),
      tasks: workspace.tasks.filter((item) => item.matter_id === matterId),
      timeline: workspace.timeline.filter((item) => item.matter_id === matterId),
      versions: [],
    };
  },

  create(brand, payload) {
    const { all, workspace } = readBrand(brand);
    if (workspace.matters.some((item) => item.id === payload.id)) throw new Error("Matter reference already exists in this session");
    const now = new Date().toISOString();
    workspace.matters.unshift({ ...payload, status: payload.status || "Open", created_at: now, updated_at: now });
    record(workspace, payload.id, "Created session matter workspace");
    saveBrand(brand, all, workspace);
    return { ok: true };
  },

  update(brand, matterId, payload) {
    const { all, workspace } = readBrand(brand);
    const index = workspace.matters.findIndex((item) => item.id === matterId);
    if (index === -1) throw new Error("Matter not found in this browser session");
    workspace.matters[index] = { ...workspace.matters[index], ...payload, id: matterId, updated_at: new Date().toISOString() };
    record(workspace, matterId, "Updated session matter workspace");
    saveBrand(brand, all, workspace);
    return { ok: true };
  },

  createTask(brand, matterId, taskTitle) {
    const { all, workspace } = readBrand(brand);
    workspace.tasks.push({ id: id("task"), matter_id: matterId, task_title: taskTitle, status: "Pending" });
    record(workspace, matterId, `Added task: ${taskTitle}`);
    saveBrand(brand, all, workspace);
    return { ok: true };
  },

  updateTask(brand, matterId, taskId, status) {
    const { all, workspace } = readBrand(brand);
    const task = workspace.tasks.find((item) => item.id === taskId && item.matter_id === matterId);
    if (!task) throw new Error("Task not found");
    task.status = status;
    record(workspace, matterId, `Marked task ${status.toLowerCase()}`);
    saveBrand(brand, all, workspace);
    return { ok: true };
  },

  deleteTask(brand, matterId, taskId) {
    const { all, workspace } = readBrand(brand);
    workspace.tasks = workspace.tasks.filter((item) => item.id !== taskId || item.matter_id !== matterId);
    record(workspace, matterId, "Removed task");
    saveBrand(brand, all, workspace);
    return { ok: true };
  },

  createDocument(brand, matterId, payload) {
    const { all, workspace } = readBrand(brand);
    const document = { id: id("document"), matter_id: matterId, ...payload, created_at: new Date().toISOString() };
    workspace.documents.unshift(document);
    record(workspace, matterId, `Attached session document: ${payload.filename}`);
    saveBrand(brand, all, workspace);
    return document;
  },

  deleteDocument(brand, matterId, documentId) {
    const { all, workspace } = readBrand(brand);
    workspace.documents = workspace.documents.filter((item) => item.id !== documentId || item.matter_id !== matterId);
    record(workspace, matterId, "Removed session document");
    saveBrand(brand, all, workspace);
    return { ok: true };
  },
};
